import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CreditCard, MapPin, Calendar, Users, ShieldCheck, CheckCircle2, Wallet, Coins } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => `€${(Number(n) || 0).toFixed(2)}`;

export default function DepositPage() {
  const { ref } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null); // "deposit" | "full" | null
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        // SEC-002 (iter73): booking lookup requires an unguessable
        // `?token=…` for any booking created after 2026-07. Confirmation
        // emails include the token in the deposit-page link so the
        // customer never has to know it exists.
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");
        const qs = token ? `?token=${encodeURIComponent(token)}` : "";
        const { data } = await axios.get(`${API}/bookings/lookup/${ref}${qs}`);
        setBooking(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Booking reference not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [ref]);

  const startCheckout = async (mode) => {
    setPaying(mode);
    try {
      const { data } = await axios.post(`${API}/deposit/checkout/${ref}`, { mode });
      window.location.href = data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start payment.";
      toast.error(msg);
      setPaying(null);
    }
  };

  return (
    <div data-testid="deposit-page" className="min-h-screen bg-[#050505] text-zinc-100">
      <Header />

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C5A059]">Secure Payment</p>
          <h1 className="font-display text-4xl sm:text-5xl text-white font-light mt-3">
            Confirm your booking
          </h1>
          <p className="text-sm text-zinc-400 mt-3 max-w-lg">
            Choose how you&rsquo;d like to pay. Either lock in your booking with a small deposit and
            pay the balance in cash on the day, or settle the full amount online now.
          </p>

          <div className="mt-10 rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 sm:p-8">
            {loading && (
              <div className="flex items-center gap-3 text-sm text-zinc-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading your booking…
              </div>
            )}

            {!loading && err && (
              <div data-testid="deposit-error" className="text-sm text-red-300">{err}</div>
            )}

            {!loading && booking && (
              <>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Reference</p>
                <p className="text-2xl font-display text-white mt-1">{booking.ref}</p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <Item icon={<Calendar className="w-4 h-4 text-[#C5A059]" />} label="Pickup"
                        value={`${booking.pickup_date} · ${booking.pickup_time}`} />
                  <Item icon={<Users className="w-4 h-4 text-[#C5A059]" />} label="Passengers"
                        value={`${booking.passengers} · ${booking.vehicle_type || "Comfort Class"}`} />
                  <Item icon={<MapPin className="w-4 h-4 text-emerald-400" />} label="From"
                        value={booking.pickup_location} />
                  <Item icon={<MapPin className="w-4 h-4 text-[#C5A059]" />} label="To"
                        value={booking.dropoff_location || "Hourly charter"} />
                </div>

                <div className="mt-8 pt-6 border-t border-white/5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-zinc-400">Total trip price</span>
                    <span data-testid="deposit-total" className="text-zinc-100 font-medium tabular-nums">{eur(booking.quote_amount)}</span>
                  </div>
                </div>

                {booking.deposit_paid > 0 ? (
                  <div
                    data-testid="deposit-already-paid"
                    className="mt-8 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    <p className="text-sm text-emerald-300">
                      We&rsquo;ve received {eur(booking.deposit_paid)} for this booking. Thank you — we&rsquo;ll see you on the day.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Deposit option */}
                      <button
                        onClick={() => startCheckout("deposit")}
                        disabled={paying !== null || !booking.deposit_amount}
                        data-testid="deposit-pay-btn"
                        className={`text-left rounded-2xl border p-5 transition-all group ${
                          paying === "deposit"
                            ? "border-[#C5A059] bg-[#C5A059]/5"
                            : "border-white/10 hover:border-[#C5A059]/60 hover:bg-white/5"
                        } disabled:opacity-60`}
                      >
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">
                          <Coins className="w-3.5 h-3.5" /> Pay {booking.deposit_pct}% deposit
                        </div>
                        <p className="mt-2 text-3xl font-display text-white tabular-nums">{eur(booking.deposit_amount)}</p>
                        <p className="mt-3 text-xs text-zinc-500 leading-relaxed">
                          Remaining {eur((booking.quote_amount || 0) - (booking.deposit_amount || 0))} paid in cash to your driver on the day.
                        </p>
                        <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-[#C5A059] uppercase tracking-[0.15em]">
                          {paying === "deposit" ? "Redirecting…" : "Continue"} <CreditCard className="w-3.5 h-3.5" />
                        </span>
                      </button>

                      {/* Full-payment option */}
                      <button
                        onClick={() => startCheckout("full")}
                        disabled={paying !== null || !booking.quote_amount}
                        data-testid="full-pay-btn"
                        className={`text-left rounded-2xl border p-5 transition-all ${
                          paying === "full"
                            ? "border-[#C5A059] bg-[#C5A059]/5"
                            : "border-[#C5A059]/40 bg-[#C5A059]/5 hover:border-[#C5A059]"
                        } disabled:opacity-60`}
                      >
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">
                          <Wallet className="w-3.5 h-3.5" /> Pay 100% now
                        </div>
                        <p className="mt-2 text-3xl font-display text-[#C5A059] tabular-nums">{eur(booking.quote_amount)}</p>
                        <p className="mt-3 text-xs text-zinc-400 leading-relaxed">
                          Nothing to pay on the day. Fully cashless — hop in the car and enjoy the ride.
                        </p>
                        <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-[#C5A059] uppercase tracking-[0.15em]">
                          {paying === "full" ? "Redirecting…" : "Continue"} <CreditCard className="w-3.5 h-3.5" />
                        </span>
                      </button>
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 text-center mt-6 flex items-center justify-center gap-2">
                      <ShieldCheck className="w-3 h-3" />
                      Payment secured by Stripe · Card details never touch our servers
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Item({ icon, label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5">
        {icon} {label}
      </p>
      <p className="text-zinc-200 mt-1">{value}</p>
    </div>
  );
}
